import React, { useCallback, useEffect, useState } from "react";
import {
  Table,
  Pagination,
  Card,
  Statistic,
  Row,
  Col,
  Radio,
  Button,
  Modal,
  message,
} from "antd";
import moment from "moment";
import {
  getReports,
  getReportStats,
  REPORT_REASON_LABELS,
  CONTENT_TYPE_LABELS,
} from "../../Services/moderationApi";
import StatusPill from "../../Components/Moderation/StatusPill";
import SlaTimer from "../../Components/Moderation/SlaTimer";
import ReportDetail from "./ReportDetail";

const PAGE_SIZE = 20;

const FILTERS = [
  { key: "pending", label: "Pending" },
  { key: "overdue", label: "Overdue" },
  { key: "actioned", label: "Actioned" },
  { key: "dismissed", label: "Dismissed" },
  { key: "all", label: "All" },
];

const Reports = () => {
  const [filter, setFilter] = useState("pending");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    pending: 0,
    overdue: 0,
    actioned_today: 0,
    appeals: 0,
  });
  const [openId, setOpenId] = useState(null);

  const loadStats = useCallback(async () => {
    try {
      const resp = await getReportStats();
      if (resp?.data?.data) setStats(resp.data.data);
    } catch (err) {
      // stats bar is non-critical, stay quiet
    }
  }, []);

  const loadReports = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: PAGE_SIZE };
      if (filter === "overdue") params.isOverdue = true;
      else if (filter !== "all") params.status = filter;

      const resp = await getReports(params);
      const payload = resp?.data?.data || {};
      setRows(payload.items || []);
      setTotal(payload.total || 0);
    } catch (err) {
      message.error("Could not load reports");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [filter, page]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const refreshAll = () => {
    loadReports();
    loadStats();
  };

  const columns = [
    {
      title: "Reported user",
      dataIndex: "accused",
      render: (a) => (a && a.name ? a.name : "(no name)"),
    },
    {
      title: "Reason",
      dataIndex: "reason",
      render: (r) => REPORT_REASON_LABELS[r] || r,
      responsive: ["md"],
    },
    {
      title: "Content",
      dataIndex: "content_type",
      render: (c) => CONTENT_TYPE_LABELS[c] || c,
      responsive: ["md"],
    },
    {
      title: "Reported by",
      dataIndex: "reporter",
      render: (a) => (a && a.name ? a.name : "(no name)"),
      responsive: ["lg"],
    },
    {
      title: "Created",
      dataIndex: "created_at",
      render: (d) => (d ? moment(d).format("DD MMM HH:mm") : "—"),
      responsive: ["lg"],
    },
    {
      title: "SLA",
      dataIndex: "sla_deadline",
      render: (d, row) => <SlaTimer deadline={d} status={row.status} />,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (s) => <StatusPill status={s} />,
    },
    {
      title: "",
      key: "action",
      render: (_, row) => (
        <Button
          size="small"
          type="primary"
          onClick={() => setOpenId(row.id || row._key)}
        >
          Review
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: "0 4px" }}>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Pending" value={stats.pending} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic
              title="Overdue"
              value={stats.overdue}
              valueStyle={stats.overdue > 0 ? { color: "#cf1322" } : undefined}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Actioned Today" value={stats.actioned_today} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Open Appeals" value={stats.appeals} />
          </Card>
        </Col>
      </Row>

      <Radio.Group
        value={filter}
        onChange={(e) => {
          setPage(1);
          setFilter(e.target.value);
        }}
        style={{ marginBottom: 16 }}
      >
        {FILTERS.map((f) => (
          <Radio.Button key={f.key} value={f.key}>
            {f.label}
          </Radio.Button>
        ))}
      </Radio.Group>

      <Table
        rowKey={(r) => r.id || r._key}
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={false}
      />

      <div className="pagination" style={{ marginTop: 16, textAlign: "right" }}>
        <Pagination
          total={total}
          pageSize={PAGE_SIZE}
          current={page}
          showSizeChanger={false}
          onChange={(p) => setPage(p)}
        />
      </div>

      <Modal
        title="Report review"
        visible={!!openId}
        onCancel={() => setOpenId(null)}
        footer={null}
        width={720}
        destroyOnClose
      >
        {openId ? (
          <ReportDetail reportId={openId} onActioned={refreshAll} />
        ) : null}
      </Modal>
    </div>
  );
};

export default Reports;
